import { motion } from "framer-motion";
import { Star, Quote, ClipboardCheck, SprayCan, Wind, BadgeCheck } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  {
    icon: ClipboardCheck,
    title: "Inspection & Plan",
    text: "We walk through your space and map a room-by-room cleaning protocol before touching a thing.",
  },
  {
    icon: SprayCan,
    title: "Systematic Deep Clean",
    text: "Trained teams work top-to-bottom with professional machines and safe, proven solutions.",
  },
  {
    icon: Wind,
    title: "Sanitize & Refresh",
    text: "Shampoo extraction, descaling and disinfection leave surfaces hygienic — not just tidy.",
  },
  {
    icon: BadgeCheck,
    title: "Quality Sign-off",
    text: "A supervised final walkthrough with photo & video proof shared to your WhatsApp.",
  },
];

const REVIEWS = [
  {
    quote:
      "Being in the UK, I worried about my Kerala home. PK Hygiene handled everything — keys, cleaning, and sent me photos of every room. Completely trustworthy.",
    name: "Expat Homeowner",
    meta: "Villa · United Kingdom",
  },
  {
    quote:
      "The most systematic cleaning team we have hired. They covered our furniture, worked room by room, and the house felt brand new when they left.",
    name: "Villa Owner",
    meta: "Angamaly",
  },
  {
    quote:
      "Careful with our property, punctual, and the sofa shampooing removed stains we thought were permanent. Highly recommended for flats and offices.",
    name: "Flat Resident",
    meta: "Kochi",
  },
];

export default function Trust() {
  return (
    <section id="reviews" className="relative py-24 lg:py-36" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono-accent text-xs font-semibold uppercase tracking-[0.28em] text-[#6CBE45] mb-5 text-center"
        >
          Trust & Process
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-center max-w-3xl mx-auto"
        >
          A method behind every{" "}
          <span className="font-serif-accent italic font-semibold text-[#4A8F2A]">
            sparkle
          </span>
        </motion.h2>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
              className="relative rounded-2xl bg-white border border-[#6CBE45]/12 p-7 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-16px_rgba(74,143,42,0.28)] transition-[transform,box-shadow] duration-500"
              data-testid={`process-step-${i + 1}`}
            >
              <span className="absolute top-5 right-6 font-mono-accent text-xs font-semibold text-[#D4AF37]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="w-11 h-11 rounded-xl bg-[#F0F9EF] flex items-center justify-center">
                <s.icon className="w-5.5 h-5.5 text-[#5BA357]" />
              </span>
              <h3 className="mt-5 text-lg font-semibold tracking-tight">{s.title}</h3>
              <p className="mt-2.5 text-sm text-[#4A5A5E] leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 grid md:grid-cols-3 gap-6 lg:gap-8">
          {REVIEWS.map((r, i) => (
            <motion.blockquote
              key={r.name + i}
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
              className="relative rounded-2xl bg-gradient-to-b from-white to-[#FDFBF2] border border-[#D4AF37]/25 p-8 hover:border-[#D4AF37]/60 transition-colors duration-500"
              data-testid={`testimonial-${i + 1}`}
            >
              <Quote className="w-8 h-8 text-[#D4AF37]/50" />
              <div className="mt-4 flex gap-1" aria-label="5 star rating">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                ))}
              </div>
              <p className="mt-4 font-serif-accent italic text-lg leading-relaxed text-[#0F1A1C]">
                “{r.quote}”
              </p>
              <footer className="mt-6 pt-5 border-t border-[#D4AF37]/20">
                <p className="font-semibold text-sm">{r.name}</p>
                <p className="text-xs text-[#4A5A5E] font-mono-accent uppercase tracking-[0.14em] mt-0.5">
                  {r.meta}
                </p>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
